import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ArrowUpRight, Sparkles } from "lucide-react";
import { Reveal, SectionBadge } from "@/components/Reveal";
import { CountryPicker, DialCodePicker } from "@/components/CountryPicker";
import { useLang } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-lg border border-[#262930] bg-[#121418] px-4 py-3 md:py-3.5 text-base md:text-sm text-[#EAE6E1] placeholder:text-[#686D76] outline-none transition-colors duration-200 focus:border-[#8C9A86]";

export default function Contact() {
  const { t, lang } = useLang();
  const budgets = t("contact.budgets");
  const [form, setForm] = useState({
    firstName: "",
    familyName: "",
    email: "",
    country: "",
    dialCode: "",
    phone: "",
    projectScope: "",
    budget: "",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [polishing, setPolishing] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.firstName.trim() || !form.familyName.trim() || !form.projectScope.trim() || !form.message.trim() || !form.budget) {
      toast.error(t("contact.errFill"));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error(t("contact.errEmail"));
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, {
        ...form,
        phone: form.phone.trim() ? `${form.dialCode} ${form.phone.trim()}` : "",
      });
      toast.success(t("contact.okSent"));
      setForm({
        firstName: "",
        familyName: "",
        email: "",
        country: "",
        dialCode: "",
        phone: "",
        projectScope: "",
        budget: "",
        message: "",
      });
    } catch {
      toast.error(t("contact.errSend"));
    } finally {
      setSending(false);
    }
  };

  const polish = async () => {
    const source = form.projectScope.trim() || form.message.trim();
    if (source.length < 10) {
      toast.error(t("contact.errIdea"));
      return;
    }
    setPolishing(true);
    try {
      const { data } = await axios.post(`${API}/brief`, { text: source, lang });
      setForm((f) => ({ ...f, projectScope: data.brief }));
      toast.success(t("contact.okPolish"));
    } catch {
      toast.error(t("contact.errPolish"));
    } finally {
      setPolishing(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="border-t border-[#262930] bg-[#0E1013] py-20 md:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <Reveal className="lg:col-span-5">
          <SectionBadge label={t("contact.badge")} testId="contact-badge" />
          <h2 className="font-display mt-4 md:mt-5 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-snug text-[#EAE6E1]">
            {t("contact.heading")}
          </h2>
          <p className="mt-4 max-w-md text-base text-[#9E9E9E] leading-relaxed">
            {t("contact.sub")}
          </p>
        </Reveal>

        <Reveal className="lg:col-span-7" delay={0.1}>
          <form
            data-testid="enquiry-form"
            onSubmit={submit}
            className="rounded-2xl border border-[#262930] bg-[#121418] p-5 md:p-9"
          >
            <div className="grid grid-cols-2 gap-3 md:gap-5">
              <div>
                <label htmlFor="enquiry-first-name" className="mb-2 block truncate font-tech text-[8px] md:text-[10px] uppercase tracking-[0.15em] md:tracking-[0.2em] text-[#686D76]">
                  {t("contact.firstName")}
                </label>
                <input
                  id="enquiry-first-name"
                  data-testid="enquiry-input-first-name"
                  value={form.firstName}
                  onChange={set("firstName")}
                  placeholder="Ada"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="enquiry-family-name" className="mb-2 block truncate font-tech text-[8px] md:text-[10px] uppercase tracking-[0.15em] md:tracking-[0.2em] text-[#686D76]">
                  {t("contact.familyName")}
                </label>
                <input
                  id="enquiry-family-name"
                  data-testid="enquiry-input-family-name"
                  value={form.familyName}
                  onChange={set("familyName")}
                  placeholder="Lovelace"
                  className={inputCls}
                />
              </div>
            </div>

            <div className="mt-4 md:mt-5 grid grid-cols-2 gap-3 md:gap-5">
              <div>
                <label htmlFor="enquiry-email" className="mb-2 block truncate font-tech text-[8px] md:text-[10px] uppercase tracking-[0.15em] md:tracking-[0.2em] text-[#686D76]">
                  {t("contact.email")}
                </label>
                <input
                  id="enquiry-email"
                  data-testid="enquiry-input-email"
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="ada@yourcompany.com"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="mb-2 block truncate font-tech text-[8px] md:text-[10px] uppercase tracking-[0.15em] md:tracking-[0.2em] text-[#686D76]">
                  {t("contact.country")}
                </label>
                <CountryPicker
                  testId="enquiry-select-country"
                  value={form.country}
                  onChange={(c) => setForm((f) => ({ ...f, country: c.name, dialCode: c.dial }))}
                />
              </div>
            </div>

            <div className="mt-4 md:mt-5">
              <label htmlFor="enquiry-phone" className="mb-2 block truncate font-tech text-[8px] md:text-[10px] uppercase tracking-[0.15em] md:tracking-[0.2em] text-[#686D76]">
                {t("contact.phone")}
              </label>
              <div className="grid grid-cols-[130px_1fr] md:grid-cols-[150px_1fr] gap-3">
                <DialCodePicker
                  testId="enquiry-select-dial-code"
                  value={form.dialCode}
                  onChange={(d) => setForm((f) => ({ ...f, dialCode: d }))}
                />
                <input
                  id="enquiry-phone"
                  data-testid="enquiry-input-phone"
                  type="tel"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="555 010 2030"
                  className={inputCls}
                />
              </div>
            </div>

            <div className="mt-4 md:mt-5 grid grid-cols-2 gap-3 md:gap-5">
              <div>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <label htmlFor="enquiry-scope" className="block truncate font-tech text-[8px] md:text-[10px] uppercase tracking-[0.15em] md:tracking-[0.2em] text-[#686D76]">
                    {t("contact.scope")}
                  </label>
                  <button
                    type="button"
                    data-testid="enquiry-ai-polish-button"
                    onClick={polish}
                    disabled={polishing}
                    className="flex shrink-0 items-center gap-1 p-2 -m-2 font-tech text-[8px] md:text-[9px] uppercase tracking-[0.12em] text-[#8C9A86] transition-colors duration-200 hover:text-[#9FAF98] disabled:opacity-50"
                  >
                    {polishing ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Sparkles className="h-3 w-3" />
                    )}
                    {polishing ? t("contact.polishing") : t("contact.polish")}
                  </button>
                </div>
                <input
                  id="enquiry-scope"
                  data-testid="enquiry-input-scope"
                  value={form.projectScope}
                  onChange={set("projectScope")}
                  placeholder={t("contact.scopePlaceholder")}
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="enquiry-budget" className="mb-2 block truncate font-tech text-[8px] md:text-[10px] uppercase tracking-[0.15em] md:tracking-[0.2em] text-[#686D76]">
                  {t("contact.budget")}
                </label>
                <select
                  id="enquiry-budget"
                  data-testid="enquiry-select-budget"
                  value={form.budget}
                  onChange={set("budget")}
                  className={`${inputCls} appearance-none cursor-pointer ${form.budget ? "" : "text-[#686D76]"}`}
                >
                  <option value="" disabled className="bg-[#121418]">
                    {t("contact.budgetPlaceholder")}
                  </option>
                  {budgets.map((b) => (
                    <option key={b} value={b} className="bg-[#121418]">
                      {b}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-4 md:mt-5">
              <label htmlFor="enquiry-message" className="mb-2 block truncate font-tech text-[8px] md:text-[10px] uppercase tracking-[0.15em] md:tracking-[0.2em] text-[#686D76]">
                {t("contact.message")}
              </label>
              <textarea
                id="enquiry-message"
                data-testid="enquiry-input-message"
                rows={5}
                value={form.message}
                onChange={set("message")}
                placeholder={t("contact.messagePlaceholder")}
                className={`${inputCls} resize-none`}
              />
            </div>

            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={sending}
              className="group mt-6 md:mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-[#8C9A86] px-7 py-3.5 md:py-4 text-sm font-semibold text-[#0B0C0E] transition-colors duration-200 hover:bg-[#9FAF98] disabled:opacity-60"
            >
              {sending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t("contact.sending")}
                </>
              ) : (
                <>
                  {t("contact.submit")}
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </>
              )}
            </button>
            <p
              data-testid="enquiry-inbox-note"
              className="mt-4 text-center font-tech text-[10px] uppercase tracking-[0.15em] text-[#686D76]"
            >
              {t("contact.note")}
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
